// widgets/NutritionAnalysisWidget.tsx
import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Utensils, Sparkles, TrendingUp, Target, Award, AlertCircle, CheckCircle, Brain, Flame } from "lucide-react";

const NutritionAnalysisWidget: React.FC = () => {
    const { t, i18n } = useTranslation("healthSensors");
    const isRTL = ["fa", "ar"].includes(i18n.language);

    const tabs = t("tabs", { returnObjects: true }) as any[];
    const nutritionTab = tabs.find((tab) => tab.id === "nutritionAnalysis");
    const widget = nutritionTab?.widget || {};

    return (
        <motion.div
            className="relative w-full"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
        >
            {/* Main widget */}
            <div className="bg-gradient-to-br from-white/90 via-emerald-50/30 to-teal-100/20 backdrop-blur-xl rounded-3xl p-6 shadow-2xl border border-emerald-200/40 mb-4 overflow-hidden relative">
                {/* Background effects */}
                <div className={`absolute ${isRTL ? "left-10" : "right-10"} -top-10 w-40 h-40 bg-gradient-to-br from-emerald-200 to-teal-100 rounded-full opacity-20 blur-2xl`}></div>

                {/* Header */}
                <div className="flex justify-between items-center mb-6 relative z-10">
                    <div className="flex items-center">
                        <motion.div
                            className={`me-3 p-3 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg`}
                            animate={{ rotate: [0, 360] }}
                            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                        >
                            <Brain className="w-6 h-6 text-white" />
                        </motion.div>
                        <div>
                            <h3 className="text-xl font-bold text-emerald-900 mb-1">{widget.title}</h3>
                            <p className="text-sm text-emerald-600">{widget.subtitle}</p>
                        </div>
                    </div>
                    <motion.div
                        className="flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-4 py-2 rounded-2xl shadow-lg"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        <Sparkles className="w-4 h-4" />
                        <span className="text-sm font-semibold">{widget.aiPowered}</span>
                    </motion.div>
                </div>

                {/* Daily nutrition score */}
                <div className="mb-6 relative z-10">
                    <motion.div
                        className="p-6 bg-white/60 backdrop-blur-md rounded-2xl border border-emerald-200/50 shadow-lg"
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.4 }}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h4 className="text-lg font-bold text-gray-800">{widget.dailyScore?.title}</h4>
                                <p className="text-sm text-gray-600">{widget.dailyScore?.date}</p>
                            </div>
                            <motion.div className="relative" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.6, type: "spring", stiffness: 200 }}>
                                <svg width="80" height="80" viewBox="0 0 80 80">
                                    <circle cx="40" cy="40" r="32" fill="none" stroke="#e5e7eb" strokeWidth="8" />
                                    <motion.circle
                                        cx="40"
                                        cy="40"
                                        r="32"
                                        fill="none"
                                        stroke="url(#nutritionGradient)"
                                        strokeWidth="8"
                                        strokeLinecap="round"
                                        strokeDasharray={`${2 * Math.PI * 32}`}
                                        initial={{ strokeDashoffset: 2 * Math.PI * 32 }}
                                        animate={{ strokeDashoffset: 2 * Math.PI * 32 * (1 - (widget.dailyScore?.value || 0) / 100) }}
                                        transition={{ duration: 2, ease: "easeInOut" }}
                                        style={{ transform: "rotate(-90deg)", transformOrigin: "50% 50%" }}
                                    />
                                    <defs>
                                        <linearGradient id="nutritionGradient">
                                            <stop offset="0%" stopColor="#10b981" />
                                            <stop offset="100%" stopColor="#14b8a6" />
                                        </linearGradient>
                                    </defs>
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <span className="text-2xl font-bold text-emerald-700">{widget.dailyScore?.value}</span>
                                </div>
                            </motion.div>
                        </div>

                        {/* Macros breakdown */}
                        <div className="grid grid-cols-3 gap-3">
                            {widget.macros?.map((macro: any, index: number) => (
                                <motion.div
                                    key={index}
                                    className="text-center p-3 bg-gradient-to-br from-white to-emerald-50/50 rounded-xl"
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: 0.8 + index * 0.1 }}
                                >
                                    <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br ${macro.color} text-white mb-2`}>
                                        {macro.icon === "flame" && <Flame className="w-5 h-5" />}
                                        {macro.icon === "target" && <Target className="w-5 h-5" />}
                                        {macro.icon === "award" && <Award className="w-5 h-5" />}
                                    </div>
                                    <div className="text-lg font-bold text-gray-800">
                                        {macro.value}
                                        {macro.unit}
                                    </div>
                                    <div className="text-xs text-gray-600">{macro.label}</div>
                                    <div className="text-xs text-emerald-600 mt-1">{macro.percentage}%</div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>

                {/* Meals analysis */}
                <div className="space-y-3 mb-6 relative z-10">
                    <h4 className="text-sm font-semibold text-emerald-900 mb-4 flex items-center">
                        <Utensils className={`w-4 h-4 me-2 text-emerald-600`} />
                        {widget.meals?.title}
                    </h4>

                    {widget.meals?.items?.map((meal: any, index: number) => (
                        <motion.div
                            key={index}
                            className="p-4 bg-white/50 backdrop-blur-sm rounded-xl border border-emerald-100/50"
                            initial={{ x: -20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.6 + index * 0.1 }}
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <div className="text-3xl">{meal.icon}</div>
                                    <div>
                                        <h5 className="text-sm font-semibold text-gray-800">{meal.name}</h5>
                                        <p className="text-xs text-gray-500">{meal.time}</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-lg font-bold text-gray-800">{meal.calories}</div>
                                    <div className="text-xs text-gray-500">{widget.caloriesLabel}</div>
                                </div>
                            </div>

                            {/* Meal quality */}
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-600">{widget.qualityLabel}</span>
                                <div className="flex items-center gap-2">
                                    <div className="flex gap-1">
                                        {[...Array(5)].map((_, i) => (
                                            <motion.div
                                                key={i}
                                                className={`w-2 h-2 rounded-full ${i < meal.quality ? "bg-emerald-500" : "bg-gray-300"}`}
                                                initial={{ scale: 0 }}
                                                animate={{ scale: 1 }}
                                                transition={{ delay: 1 + index * 0.1 + i * 0.05 }}
                                            />
                                        ))}
                                    </div>
                                    <span className="text-xs font-semibold text-emerald-600">{meal.qualityText}</span>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* AI Nutrition Insights */}
            <motion.div
                className="bg-gradient-to-br from-emerald-50/60 to-teal-50/40 backdrop-blur-xl rounded-2xl p-4 border border-emerald-200/50 shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
            >
                <div className="flex items-center mb-3">
                    <motion.div
                        className={`w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center me-2 shadow-lg`}
                        animate={{ rotate: [0, 360] }}
                        transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                    >
                        <Sparkles className="w-5 h-5 text-white" />
                    </motion.div>
                    <div>
                        <h4 className="font-bold text-emerald-900">{widget.insights?.title}</h4>
                        <p className="text-xs text-emerald-600">{widget.insights?.subtitle}</p>
                    </div>
                </div>

                {/* Strengths */}
                <div className="mb-4">
                    <h5 className="text-sm font-semibold text-emerald-900 mb-2 flex items-center">
                        <CheckCircle className={`w-4 h-4 ${isRTL ? "mr-1.5" : "ml-1.5"} text-green-600`} />
                        {widget.insights?.strengths?.title}
                    </h5>
                    <div className="space-y-2">
                        {widget.insights?.strengths?.items?.map((item: string, index: number) => (
                            <motion.div
                                key={index}
                                className="flex items-start gap-2 p-2 bg-white/60 backdrop-blur-sm rounded-lg"
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: 0.7 + index * 0.1 }}
                            >
                                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-gray-700">{item}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Improvements */}
                <div className="mb-4">
                    <h5 className="text-sm font-semibold text-emerald-900 mb-2 flex items-center">
                        <TrendingUp className={`w-4 h-4 ${isRTL ? "mr-1.5" : "ml-1.5"} text-amber-600`} />
                        {widget.insights?.improvements?.title}
                    </h5>
                    <div className="space-y-2">
                        {widget.insights?.improvements?.items?.map((item: string, index: number) => (
                            <motion.div
                                key={index}
                                className="flex items-start gap-2 p-2 bg-white/60 backdrop-blur-sm rounded-lg"
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: 0.9 + index * 0.1 }}
                            >
                                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-gray-700">{item}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Personalized recommendation */}
                {widget.insights?.personalizedTip && (
                    <motion.div
                        className="p-3 bg-gradient-to-r from-emerald-100 to-teal-100 rounded-xl border border-emerald-200"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.2 }}
                    >
                        <div className="flex items-start gap-2">
                            <Sparkles className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                            <div>
                                <h6 className="text-sm font-semibold text-emerald-900 mb-1">{widget.insights?.personalizedTip?.title}</h6>
                                <p className="text-sm text-emerald-800">{widget.insights?.personalizedTip?.text}</p>
                            </div>
                        </div>
                    </motion.div>
                )}
            </motion.div>
        </motion.div>
    );
};

export default NutritionAnalysisWidget;
